import { Card, Stack, Table, Button, TableBody, Typography, TableContainer } from '@mui/material';

import { useState, useEffect, useCallback } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';

import { Pagination } from 'src/type/utils';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fParams, ParamsType } from 'src/utils/format-params';
import usePagination from 'src/hooks/use-pagination';
import Scrollbar from 'src/components/scrollbar';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableLoading from 'src/components/table/table-loading';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { emptyRows } from 'src/components/table/utils';
import Iconify from 'src/components/iconify';
import { ProductType, ProductRedeemFeeType } from 'src/type/product';
import ProductFeeTableRow from './product-fee-table-row';
import ProductFeeFormDialog from './product-fee-form-dialog';

const tableHead = [
  { id: 'name', label: 'ชื่อบล็อค' },
  { id: 'weightGram', label: 'นน. (กรัม)', width: 120, align: 'right' },
  { id: 'weightBaht', label: 'นน. (บาท)', width: 120, align: 'right' },
  { id: 'fee', label: 'ค่าธรรมเนียม', width: 150, align: 'right' },
  { id: 'action', width: 60 },
];

type Props = {
  product: ProductType;
};

export default function ProductFeeTable({ product }: Props) {
  const initialize = useBoolean(true);
  const reload = useBoolean(false);
  const form = useBoolean(false);
  const loading = useBoolean();
  const [edit, setEdit] = useState<ProductRedeemFeeType | null>(null);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<ProductRedeemFeeType>({ defaultRowsPerPage: 10 });

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      limit: rowsPerPage,
      productId: product.id,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
      orderBy: 'weightGram:asc',
    };
    handleGetTx(params);
  }, [currentPage, rowsPerPage, reload.value]);

  useEffect(() => {
    if (!form.value) {
      setEdit(null);
    }
  }, [form.value]);

  const handleGetTx = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();

      const { data } = await axios.get<Pagination<ProductRedeemFeeType>>(
        endpoints.productRedeemFees.get,
        {
          params: fParams(params),
        }
      );

      // console.log({ data });

      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    reload.onToggle();
  }, []);

  const handleEdit = useCallback(async (fee: ProductRedeemFeeType) => {
    setEdit(fee);
    form.onTrue();
  }, []);

  const handleDelete = useCallback(async (fee: ProductRedeemFeeType) => {
    try {
      loading.onTrue();
      await axios.delete(endpoints.productRedeemFees.delete(fee.id));
      handleReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  return (
    <>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6" gutterBottom>
            ค่าบล็อค {product.name}
          </Typography>
          <Button
            onClick={form.onTrue}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            สร้างรายการค่าบล็อค
          </Button>
        </Stack>

        <Card>
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <ProductFeeTableRow
                        key={row.id}
                        row={row}
                        loading={loading.value}
                        onEdit={() => handleEdit(row)}
                        onDelete={() => handleDelete(row)}
                      />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>

      <ProductFeeFormDialog
        open={form.value}
        onClose={form.onFalse}
        handleReload={handleReload}
        product={product}
        productFee={edit}
      />
    </>
  );
}
